<!--
  A mermaid diagram.

  The host <div> is declared EMPTY in this template on purpose. Svelte only
  patches DOM it is told to render, so an element with no template children is
  one it will never diff -- which means mermaid can own that subtree completely
  and the SVG it injects survives every live update. The source text goes in
  with textContent (not innerHTML; nothing in this app uses innerHTML) and
  mermaid.run() replaces it.

  Re-rendering is keyed on the source, so a document event that does not change
  the diagram leaves a rendered one alone. Re-running every diagram on every
  event made the whole page flash. TASK-034.

  COMMENTS (TASK-045). The host carries `data-diagram`, so a selection inside
  the SVG can be mapped to the block (lib/selection.ts). The block's comment
  ranges, on its source, are drawn by DECORATING what mermaid drew, in an
  effect of its own that runs after each render and whenever the ranges or the
  active thread change -- never by re-rendering, so hovering a thread does not
  redraw the diagram. A range whose words are a label's (lib/diagram.ts) tints
  every label drawn with those words; any other range -- the whole diagram, or
  words no label shows -- outlines the host. Decoration only sets fixed
  classes and attributes whose values are server thread ids and states: label
  text is attacker-written, and is only ever compared.

  The host and one pin button per thread sit in a wrapper that keeps the
  block's `data-id` on the OUTERMOST element (the scroll anchor looks for it
  there) and places a focused pin over the diagram's corner, so neither
  decorating nor focusing a pin moves the document.
-->
<script lang="ts">
  import { untrack, type Snippet } from 'svelte'
  import { loadMermaid } from '../mermaid'
  import { coverOfThreads, type BlockRange, type Cover } from '../anchors'
  import { LABEL_SELECTOR, diagramKey, diagramShown, labelKey } from '../diagram'
  import { useHighlights } from '../highlights.svelte'

  let {
    src,
    id,
    ranges = [],
    fallback,
    onrendered,
  }: {
    src: string
    id?: string
    /** This block's comment ranges to draw, in raw offsets of `src`. */
    ranges?: BlockRange[]
    /** What to show when mermaid cannot draw it: the source, as a code block. */
    fallback?: Snippet
    onrendered?: () => void
  } = $props()

  const hl = useHighlights()

  let host = $state<HTMLDivElement | null>(null)
  /** Set when mermaid cannot parse or cannot load: show the source instead. */
  let failed = $state(false)
  /** Bumped after every render, so decoration runs on the new SVG. */
  let rendered = $state(0)
  /** The threads outlining the whole diagram, if any. */
  let hostCover = $state<Cover | null>(null)

  $effect(() => {
    // Read both up front so the effect re-runs when either changes.
    const text = src
    const node = host
    if (!node) return

    let cancelled = false
    failed = false

    // mermaid.run() stamps data-processed on a node it has handled and skips
    // it forever after, so a changed diagram has to be un-stamped first.
    node.removeAttribute('data-processed')
    node.textContent = text

    loadMermaid()
      .then((mermaid) => {
        if (cancelled || !node.isConnected) return
        return mermaid.run({ nodes: [node] })
      })
      .then(() => {
        if (cancelled) return
        rendered += 1
        onrendered?.()
      })
      .catch((err) => {
        if (cancelled) return
        // Either the library did not load (a failed SRI check counts) or the
        // diagram text is not valid mermaid. Both end the same way: show the
        // reader what the agent actually wrote.
        console.warn('mermaid render failed', err)
        failed = true
        onrendered?.()
      })

    return () => {
      cancelled = true
    }
  })

  // Decoration: reads the ranges, the active thread and the render counter,
  // never `src` directly (the ranges were cut from it), so none of this
  // re-runs the render above.
  $effect(() => {
    void rendered
    const node = host
    const list = ranges
    const active = hl?.active ?? null
    if (!node) return

    for (const el of Array.from(node.querySelectorAll('.anchor-label'))) {
      el.classList.remove('anchor-label')
      el.removeAttribute('data-threads')
      el.removeAttribute('data-state')
      el.removeAttribute('data-active')
    }
    // Not drawn yet (or being redrawn): there is nothing to decorate, and an
    // outline around the empty host would flash.
    if (!node.querySelector('svg') || list.length === 0) {
      hostCover = null
      return
    }

    const text = untrack(() => src)
    const labels = Array.from(node.querySelectorAll<HTMLElement>(LABEL_SELECTOR)).map((el) => ({
      el,
      key: labelKey(el.textContent),
    }))
    const onLabel = new Map<HTMLElement, BlockRange[]>()
    const onHost: BlockRange[] = []
    for (const r of list) {
      const key = diagramKey(text.slice(r.from, r.to)).key
      // Every label drawn with these words: a label written twice is tinted twice.
      const hits = key ? labels.filter((l) => l.key === key) : []
      if (hits.length === 0) onHost.push(r)
      for (const { el } of hits) onLabel.set(el, [...(onLabel.get(el) ?? []), r])
    }
    for (const [el, rs] of onLabel) {
      const cover = coverOfThreads(rs)
      if (!cover) continue
      el.classList.add('anchor-label')
      el.setAttribute('data-threads', cover.threads.join(' '))
      el.setAttribute('data-state', cover.state)
      if (active && cover.threads.includes(active)) el.setAttribute('data-active', '')
    }
    hostCover = coverOfThreads(onHost)
  })

  /** A pin's accessible name: never the Mermaid source. */
  function pinLabel(r: BlockRange): string {
    const shown = diagramShown(r.text, src)
    return `Go to the ${r.state} comment on ${shown ? `the diagram label “${shown.length > 60 ? `${shown.slice(0, 59)}…` : shown}”` : 'the diagram'}`
  }
</script>

{#if failed}
  {@render fallback?.()}
{:else}
  <div class="diagram" data-id={id}>
    <div
      class="mermaid"
      data-diagram={id}
      data-threads={hostCover ? hostCover.threads.join(' ') : undefined}
      data-state={hostCover?.state}
      data-active={hostCover && hl?.active && hostCover.threads.includes(hl.active) ? '' : undefined}
      bind:this={host}
    ></div>
    {#each ranges as r (r.threadId)}<button
        type="button"
        class="anchor-pin"
        data-anchor-pin
        data-thread-id={r.threadId}
        data-state={r.state}
        aria-label={pinLabel(r)}
        onclick={() => hl?.activate(r.threadId)}
        onfocus={() => hl && (hl.active = r.threadId)}
        onblur={() => hl && hl.active === r.threadId && (hl.active = null)}
      ></button>{/each}
  </div>
{/if}
